import Dragger from 'antd/es/upload/Dragger';
import { AIDrivenSearchUploadFile } from '../AIDrivenSearchUploadFile';
import { AIDrivenSearchUploadClickUpload } from '../AIDrivenSearchUploadClickUpload';
import styles from './AIDrivenSearchUpload.module.scss';

/**
 * @component
 *
 * @summary
 * Controlled Excel file upload component with drag-and-drop support and validation.
 *
 * @description
 * A reusable, fully controlled upload component for Excel files (.xlsx, .xls).
 * Handles file type validation, size validation, upload progress display, and error states.
 * This component is **stateless** and delegates all state management to its parent.
 * All validation logic is self-contained for easy reusability.
 *
 * @features
 * - Drag-and-drop or click to upload
 * - Excel file type validation (.xlsx, .xls)
 * - Configurable file size limit (default: 100MB)
 * - Upload progress visualization
 * - Error state handling with user feedback
 * - Single file upload only
 *
 * @param {Object} props - Component props.
 * @param {(file: File|null, error: Object|null) => void} props.onUpload - Callback when file is
 * uploaded or validation fails. Receives (file, error) where one is always null.
 * @param {() => void} props.onDelete - Callback when uploaded file is deleted.
 * @param {boolean} [props.disabled=false] - Whether the upload is disabled.
 * @param {File|null} [props.uploadedFile=null] - Currently uploaded file (controlled state).
 * @param {Object|null} [props.error=null] - Error object with `name` and `description` properties.
 * @param {number} [props.maxSize=104857600] - Maximum file size in bytes (default: 100MB).
 * @param {string} [props.accept='.xlsx, .xls'] - Accepted file types.
 * @returns {JSX.Element} Dragger component with file upload UI.
 *
 * @example
 * // Controlled usage
 * const [file, setFile] = useState(null);
 * const [error, setError] = useState(null);
 *
 * const handleUpload = (file, error) => {
 *   if (error) {
 *     setError(error);
 *   } else {
 *     setFile(file);
 *     // Start upload...
 *   }
 * };
 *
 * <AIDrivenSearchUpload
 *   uploadedFile={file}
 *   error={error}
 *   onUpload={handleUpload}
 *   onDelete={() => setFile(null)}
 * />
 *
 * @example
 * // Minimal version
 * <AIDrivenSearchUpload
 *   maxSize={50 * 1024 * 1024} // 50MB
 *   onUpload={handleUpload}
 *   onDelete={handleDelete}
 * />
 */
export function AIDrivenSearchUpload({
  onUpload,
  onDelete,
  disabled = false,
  uploadedFile = null,
  error = null,
  maxSize = 100 * 1024 * 1024, // 100MB
  accept = '.xlsx, .xls, .xlsm',
  ...rest
}) {
  const handleBeforeUpload = file => {
    const isExcel =
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      file.type === 'application/vnd.ms-excel' ||
      file.type === 'application/vnd.ms-excel.sheet.macroEnabled.12' ||
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.xls') ||
      file.name.endsWith('.xlsm');

    if (isExcel === false) {
      onUpload(null, {
        notAccepted: true,
        name: file.name,
        description: 'Ο τύπος του αρχείου δεν είναι αποδεκτός',
      });
      return false;
    }

    const isValidSize = file.size <= maxSize;
    if (isValidSize === false) {
      onUpload(null, {
        fileLimit: true,
        name: file.name,
        description: 'The uploaded file is too large.',
      });
      return false;
    }

    onUpload(file, null);
    return false;
  };

  const handleDeleteDocument = event => {
    event.stopPropagation();
    event.preventDefault();
    onDelete();
  };

  const renderDraggerContent = () => {
    if (error) {
      return (
        <AIDrivenSearchUploadFile
          error
          file={{
            ...error,
            name: error.name,
            description: error.description,
          }}
          disabled={disabled}
          handleDeleteDocument={handleDeleteDocument}
        />
      );
    }
    if (uploadedFile) {
      return (
        <AIDrivenSearchUploadFile
          file={uploadedFile}
          disabled={disabled}
          handleDeleteDocument={handleDeleteDocument}
        />
      );
    }
    return <AIDrivenSearchUploadClickUpload />;
  };

  return (
    <Dragger
      {...rest}
      disabled={disabled}
      className={`custom-drag-upload-static ${styles.container}`}
      id={rest.id ?? Math.random().toString()}
      accept={accept}
      beforeUpload={handleBeforeUpload}
      showUploadList={false}
      multiple={false}
    >
      {renderDraggerContent()}
    </Dragger>
  );
}
